import React from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore } from "@mui/icons-material";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function RoadkillCharactersConfig({
  gameConfig,
  setGameConfig,
  loading,
}) {
  const { isViewer } = getUserPermissions();

  // Get current characters with fallback to default characters
  const getCurrentCharacters = () => {
    if (gameConfig.characters && gameConfig.characters.length > 0) {
      return gameConfig.characters;
    } else {
      return [
        {
          id: 1,
          name: "Dog",
          hire: 1,
          bonus: 8,
          lanes: [
            { multiplier: 1, winProbability: 72 },
            { multiplier: 1, winProbability: 72 },
            { multiplier: 1, winProbability: 72 },
            { multiplier: 1, winProbability: 72 },
            { multiplier: 1, winProbability: 72 },
            { multiplier: 1, winProbability: 72 },
            { multiplier: 2, winProbability: 72 }
          ]
        },
      ];
    }
  };

  const handleCharacterChange = (index, field, value) => {
    const currentCharacters = [...getCurrentCharacters()];
    currentCharacters[index] = {
      ...currentCharacters[index],
      [field]: field === 'name' ? value : parseFloat(value) || 0,
    };

    setGameConfig({ ...gameConfig, characters: currentCharacters });
  };

  const handleLaneChange = (characterIndex, laneIndex, field, value) => {
    const currentCharacters = [...getCurrentCharacters()];
    const updatedLanes = [...currentCharacters[characterIndex].lanes];
    updatedLanes[laneIndex] = {
      ...updatedLanes[laneIndex],
      [field]: parseFloat(value) || 0,
    };

    currentCharacters[characterIndex] = {
      ...currentCharacters[characterIndex],
      lanes: updatedLanes,
    };

    setGameConfig({ ...gameConfig, characters: currentCharacters });
  };

  const handleAddCharacter = () => {
    const currentCharacters = getCurrentCharacters();
    const maxId = Math.max(...currentCharacters.map(character => character.id), 0);
    const newCharacters = [
      ...currentCharacters,
      {
        id: maxId + 1,
        name: `Character ${currentCharacters.length + 1}`,
        hire: 1,
        bonus: 5,
        lanes: [
          { multiplier: 1, winProbability: 70 },
          { multiplier: 1, winProbability: 70 },
          { multiplier: 1, winProbability: 70 },
          { multiplier: 1, winProbability: 70 },
          { multiplier: 1, winProbability: 70 },
          { multiplier: 1, winProbability: 70 },
          { multiplier: 2, winProbability: 70 }
        ]
      },
    ];

    setGameConfig({ ...gameConfig, characters: newCharacters });
  };

  const handleDeleteCharacter = (index) => {
    const currentCharacters = getCurrentCharacters();
    if (currentCharacters.length <= 1) return;

    const newCharacters = currentCharacters.filter((_, i) => i !== index);

    setGameConfig({ ...gameConfig, characters: newCharacters });
  };

  return (
    <Box sx={{ mt: 2, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Roadkill Characters Configuration
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Configure roadkill characters with their hire cost, bonus, and lane settings
      </Typography>

      <Grid container spacing={3}>
        {getCurrentCharacters().map((character, index) => (
          <Grid item xs={12} key={`character-${character.id}`}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                "&:hover": {
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Character {index + 1}: {character.name}
                </Typography>
                {!isViewer && (
                  <IconButton
                    onClick={() => handleDeleteCharacter(index)}
                    disabled={loading || getCurrentCharacters().length <= 1}
                    size="small"
                    sx={{
                      color:
                        getCurrentCharacters().length <= 1 ? "#ccc" : "#d32f2f",
                      "&:hover": {
                        backgroundColor:
                          getCurrentCharacters().length <= 1
                            ? "transparent"
                            : "#ffcdd2",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Character Name"
                    value={character.name}
                    onChange={(e) =>
                      handleCharacterChange(index, "name", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#1976d2",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Hire Cost"
                    type="number"
                    value={character.hire}
                    onChange={(e) =>
                      handleCharacterChange(index, "hire", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#4caf50",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Bonus"
                    type="number"
                    value={character.bonus}
                    onChange={(e) =>
                      handleCharacterChange(index, "bonus", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#ff9800",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  aria-controls={`lanes-content-${index}`}
                  id={`lanes-header-${index}`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Lane Configuration ({character.lanes?.length || 0} lanes)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {(character.lanes || []).map((lane, laneIndex) => (
                      <Grid item xs={12} sm={6} md={4} key={`lane-${laneIndex}`}>
                        <Paper
                          sx={{
                            p: 2,
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Lane {laneIndex + 1}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Multiplier"
                                type="number"
                                size="small"
                                value={lane.multiplier}
                                onChange={(e) =>
                                  handleLaneChange(index, laneIndex, "multiplier", e.target.value)
                                }
                                variant="outlined"
                                disabled={loading}
                                inputProps={{ min: 0, step: 0.1 }}
                                sx={{
                                  "& .MuiInputLabel-root": {
                                    color: "#e91e63",
                                    fontWeight: 500,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                label="Win %"
                                type="number"
                                size="small"
                                value={lane.winProbability}
                                onChange={(e) =>
                                  handleLaneChange(index, laneIndex, "winProbability", e.target.value)
                                }
                                variant="outlined"
                                disabled={loading}
                                inputProps={{ min: 0, max: 100, step: 1 }}
                                sx={{
                                  "& .MuiInputLabel-root": {
                                    color: "#9c27b0",
                                    fontWeight: 500,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCharacter}
          disabled={loading}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Add New Character
        </Button>
      </Box>
    </Box>
  );
}
